import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Languages } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sectionOptions = [
  { value: 'nav', label: 'Navigation' },
  { value: 'hero', label: 'Hero Section' },
  { value: 'services', label: 'Services' },
  { value: 'target', label: 'Target Market' },
  { value: 'contact', label: 'Contact' },
  { value: 'footer', label: 'Footer' },
  { value: 'about', label: 'About' },
  { value: 'general', label: 'General' },
];

const ManageTranslations = () => {
  const { language } = useLanguage();
  const [translations, setTranslations] = useState([]);
  const [filteredTranslations, setFilteredTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSection, setFilterSection] = useState('all');
  const [formData, setFormData] = useState({
    key: '',
    value_en: '',
    value_id: '',
    section: 'general'
  });

  useEffect(() => {
    fetchTranslations();
  }, []);

  useEffect(() => {
    filterTranslations();
  }, [translations, searchTerm, filterSection]);

  const fetchTranslations = async () => {
    try {
      const response = await axios.get(`${API}/translations`);
      setTranslations(response.data);
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to fetch translations' : 'Gagal mengambil terjemahan');
    } finally {
      setLoading(false);
    }
  };

  const filterTranslations = () => {
    let filtered = [...translations];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        t.key.toLowerCase().includes(term) ||
        t.value_en.toLowerCase().includes(term) ||
        t.value_id.toLowerCase().includes(term)
      );
    }
    
    if (filterSection !== 'all') {
      filtered = filtered.filter(t => t.section === filterSection);
    }
    
    setFilteredTranslations(filtered);
  };

  const openCreateDialog = () => {
    setEditingTranslation(null);
    setFormData({
      key: '',
      value_en: '',
      value_id: '',
      section: 'general'
    });
    setDialogOpen(true);
  };

  const openEditDialog = (translation) => {
    setEditingTranslation(translation);
    setFormData({
      key: translation.key,
      value_en: translation.value_en,
      value_id: translation.value_id,
      section: translation.section
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTranslation) {
        await axios.put(`${API}/translations/${editingTranslation.id}`, formData);
        toast.success(language === 'en' ? 'Translation updated!' : 'Terjemahan diperbarui!');
      } else {
        await axios.post(`${API}/translations`, formData);
        toast.success(language === 'en' ? 'Translation created!' : 'Terjemahan dibuat!');
      }
      setDialogOpen(false);
      fetchTranslations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving translation');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'en' ? 'Delete this translation?' : 'Hapus terjemahan ini?')) return;
    
    try {
      await axios.delete(`${API}/translations/${id}`);
      toast.success(language === 'en' ? 'Translation deleted!' : 'Terjemahan dihapus!');
      fetchTranslations();
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to delete' : 'Gagal menghapus');
    }
  };

  if (loading) {
    return (
      <AdminLayout title={language === 'en' ? 'Manage Translations' : 'Kelola Terjemahan'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={language === 'en' ? 'Manage Translations' : 'Kelola Terjemahan'}>
      <div className="space-y-6" data-testid="manage-translations-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <p className="text-stone-600">
            {language === 'en' 
              ? 'Manage bilingual content (English & Indonesian) for the website.'
              : 'Kelola konten dwibahasa (Inggris & Indonesia) untuk website.'}
          </p>
          <Button onClick={openCreateDialog} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-translation-btn">
            <Plus className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Add Translation' : 'Tambah Terjemahan'}
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  placeholder={language === 'en' ? 'Search translations...' : 'Cari terjemahan...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-translations"
                />
              </div>
              <Select value={filterSection} onValueChange={setFilterSection}>
                <SelectTrigger className="w-48" data-testid="filter-section">
                  <SelectValue placeholder={language === 'en' ? 'All Sections' : 'Semua Bagian'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'en' ? 'All Sections' : 'Semua Bagian'}</SelectItem>
                  {sectionOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Translations Table */}
        <Card data-testid="translations-table-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Languages className="w-5 h-5 text-purple-500" />
              {language === 'en' ? 'Translations' : 'Terjemahan'} ({filteredTranslations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{language === 'en' ? 'Key' : 'Kunci'}</TableHead>
                    <TableHead>{language === 'en' ? 'English' : 'Inggris'}</TableHead>
                    <TableHead>{language === 'en' ? 'Indonesian' : 'Indonesia'}</TableHead>
                    <TableHead>{language === 'en' ? 'Section' : 'Bagian'}</TableHead>
                    <TableHead className="text-right">{language === 'en' ? 'Actions' : 'Aksi'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTranslations.map((translation, index) => (
                    <TableRow key={translation.id} data-testid={`translation-row-${index}`}>
                      <TableCell className="font-mono text-sm text-stone-600">{translation.key}</TableCell>
                      <TableCell className="max-w-xs truncate">{translation.value_en}</TableCell>
                      <TableCell className="max-w-xs truncate">{translation.value_id}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                          {translation.section}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(translation)} data-testid={`edit-translation-${index}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(translation.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-translation-${index}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {filteredTranslations.length === 0 && (
                <div className="text-center py-8 text-stone-500">
                  {language === 'en' ? 'No translations found' : 'Tidak ada terjemahan ditemukan'}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Translation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="translation-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingTranslation 
                ? (language === 'en' ? 'Edit Translation' : 'Edit Terjemahan')
                : (language === 'en' ? 'Add Translation' : 'Tambah Terjemahan')
              }
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{language === 'en' ? 'Translation Key' : 'Kunci Terjemahan'} *</Label>
              <Input
                required
                value={formData.key}
                onChange={(e) => setFormData({...formData, key: e.target.value})}
                placeholder="e.g., nav.home, hero.title"
                disabled={!!editingTranslation}
                data-testid="translation-key"
              />
              <p className="text-xs text-stone-500 mt-1">
                {language === 'en' ? 'Use dot notation for organization (e.g., section.item)' : 'Gunakan notasi titik untuk organisasi (contoh: bagian.item)'}
              </p>
            </div>

            <div>
              <Label>{language === 'en' ? 'English Value' : 'Nilai Bahasa Inggris'} *</Label>
              <Input
                required
                value={formData.value_en}
                onChange={(e) => setFormData({...formData, value_en: e.target.value})}
                data-testid="translation-en"
              />
            </div>

            <div>
              <Label>{language === 'en' ? 'Indonesian Value' : 'Nilai Bahasa Indonesia'} *</Label>
              <Input
                required
                value={formData.value_id}
                onChange={(e) => setFormData({...formData, value_id: e.target.value})}
                data-testid="translation-id"
              />
            </div>

            <div>
              <Label>{language === 'en' ? 'Section' : 'Bagian'}</Label>
              <Select value={formData.section} onValueChange={(v) => setFormData({...formData, section: v})}>
                <SelectTrigger data-testid="translation-section">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sectionOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {language === 'en' ? 'Cancel' : 'Batal'}
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-translation-btn">
                {language === 'en' ? 'Save' : 'Simpan'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ManageTranslations;
